import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {KanbanMemberSelectorUserModel, UNASSIGNED_MEMBER_ID_VALUE} from "./kanban-member-selector-user.model";
import {SelectedCollection, SelectedItem, SelectEventArgs} from "@syncfusion/ej2-lists/src/list-view/list-view";
import {FieldSettingsModel} from "@syncfusion/ej2-lists/src/list-view/list-view-model";
import {UserService} from "../../../services/user/user.service";
import {$translate} from "../../../services/internationalization/localize.service";
import {ListViewComponent} from "@syncfusion/ej2-angular-lists";
import {KanbanMemberSelectorItemModel} from "./kanban-member-selector-item.model";
import {KanbanAddMemberDialogComponent} from "../kanban-add-member-dialog/kanban-add-member-dialog.component";

@Component({
  selector: 'sb-kanban-member-selector',
  templateUrl: './kanban-member-selector.component.html',
  styleUrls: ['./kanban-member-selector.component.scss']
})
export class KanbanMemberSelectorComponent implements OnInit, OnChanges {
  @ViewChild('listViewComponent')
  listViewComponent: ListViewComponent

  @ViewChild('addMemberDialog')
  addMemberDialog: KanbanAddMemberDialogComponent

  ADD_MEMBER_ITEM_ID = 'Add-Member'
  protected readonly UNASSIGNED_MEMBER_ID_VALUE = UNASSIGNED_MEMBER_ID_VALUE;

  memberIds: string[];

  unassignedLabel: string = $translate("ng.component.kanban.unassigned")`Unassigned`
  addMemberLabel: string = $translate("ng.component.kanban.add-member.label")`Add Member`

  @Input('members')
  members: Array<KanbanMemberSelectorUserModel>

  memberItems: Array<KanbanMemberSelectorItemModel> | unknown[] = []
  fields: FieldSettingsModel = {id: 'id'};

  @Input('showDeleteMember')
  showDeleteMember: boolean = false;

  @Input('showAddMember')
  showAddMember: boolean = false;

  @Input('isMultipleSelect')
  isMultipleSelect: Boolean = false;

  @Output("membersSelected")
  membersSelectedEmitter: EventEmitter<Array<string | null>> = new EventEmitter()

  @Output("memberSelected")
  memberSelectedEmitter: EventEmitter<string> = new EventEmitter()

  @Output("newMemberSelected")
  newMemberSelectedEmitter: EventEmitter<string> = new EventEmitter()

  @Output("memberToDeleteSelected")
  memberToDeleteSelectedEmitter : EventEmitter<string> = new EventEmitter()

  constructor(private userService: UserService) {
  }

  ngOnInit(): void {
  }

  ngOnChanges(change: SimpleChanges) {
    const membersChange = change['members']
    const showAddMemberChange = change['showAddMember']
    if (membersChange || showAddMemberChange) {
      const members = (membersChange.currentValue || this.members)
      const showAddMember = showAddMemberChange?.currentValue === true || (!showAddMemberChange && this.showAddMember)
      this.loadItems(members, showAddMember);
    }
  }

  private loadItems(members: Array<KanbanMemberSelectorUserModel>, showAddMemberChange: boolean) {
    if (members) {
      this.memberItems = members
        .map(m => ({
          id: m.userId,
          user: m,
          text: this.annotateMemberName(m),
        } as KanbanMemberSelectorItemModel))
        .concat([
          {
            id: UNASSIGNED_MEMBER_ID_VALUE,
            text: this.unassignedLabel,
            miIconName: 'person',
            htmlAttributes: {'class': 'non-member-item'}
          },
        ])

      if (showAddMemberChange) {
        (this.memberItems as Array<KanbanMemberSelectorItemModel>)
          .push({
            id: this.ADD_MEMBER_ITEM_ID,
            text: this.addMemberLabel,
            miIconName: 'person_add',
            htmlAttributes: {'class': 'non-member-item add-member-item e-checkbox-hidden'}
          })
      }

      this.memberIds = members.map(m => m.userId)
    }
  }

  onSelectMember(e: SelectEventArgs) {
    if ((e.data as unknown as KanbanMemberSelectorItemModel).id == this.ADD_MEMBER_ITEM_ID) {
      this.addMemberDialog.show();

      // Unselect the item, not sure why it's done by default
      e.item.classList.remove('e-active');
      return;
    }

    if (this.isMultipleSelect) {
      // Prevent the list view from being hidden on select to allow for multiple selection
      e.event.stopPropagation();

      this.preProcessedUncheckableItems(e);
    }

    const selectedItems = this.listViewComponent.getSelectedItems()

    if (this.isMultipleSelect) {
      const selectedData = (selectedItems as SelectedCollection).data as unknown[] as KanbanMemberSelectorItemModel[];
      const memberIds = selectedData.map(d => d.id != UNASSIGNED_MEMBER_ID_VALUE ? d.id : null)
      this.membersSelectedEmitter.emit(memberIds);
    } else {
      const selectedData = (selectedItems as SelectedItem).data as unknown as KanbanMemberSelectorItemModel;
      const memberId = selectedData.id != UNASSIGNED_MEMBER_ID_VALUE ? selectedData.id : null
      this.memberSelectedEmitter.emit(memberId != UNASSIGNED_MEMBER_ID_VALUE ? memberId : null);
    }
  }

  private preProcessedUncheckableItems(e: SelectEventArgs) {
    // From: https://ej2.syncfusion.com/angular/documentation/listview/how-to/hide-checkbox-in-listview
    let normalElements: HTMLElement[] = Array.prototype.slice.call((this.listViewComponent as any).curUL.getElementsByClassName('e-checkbox-hidden'));

    // Looping through all the selected element and removing e-active class
    // to avoid behaviour interference  with getSelectedItems method
    normalElements.forEach((element) => {
      element.classList.remove('e-active');
    });
  }

  annotateMemberName(member: KanbanMemberSelectorUserModel) {
    if (this.userService.getID() === member.userId) {
      return `${member.displayName} (you)`
    } else {
      return member.displayName
    }
  }

  onNewMemberSelected(newMemberUserId: string) {
    this.newMemberSelectedEmitter.emit(newMemberUserId);
  }

  onDeleteMemberClick(event: MouseEvent, data: KanbanMemberSelectorItemModel) {
    event.stopPropagation();

    if (data.user) {
      this.memberToDeleteSelectedEmitter.emit(data.user.userId)
    }
  }
}
